import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Clock, Play, CheckCircle, XCircle, RefreshCw, AlertCircle, Database, Globe } from "lucide-react";
import { Badge } from "../components/ui/badge";

const Fmt = ({ value }) =>
  value ? <span className="font-mono text-xs">{new Date(value).toLocaleString("en-CA")}</span> : <span className="text-zinc-400">—</span>;

const Duration = ({ start, end }) => {
  if (!start) return "—";
  const from = new Date(start).getTime();
  const to = end ? new Date(end).getTime() : Date.now();
  const sec = Math.floor((to - from) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  return `${min}m ${s}s`;
};

export default function Runs() {
  const [runs, setRuns] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const reload = () => api.runs(30).then(setRuns).catch(console.error);
  useEffect(() => { reload(); const t = setInterval(reload, 8000); return () => clearInterval(t); }, []);

  return (
    <div className="p-8" data-testid="runs-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">History</div>
          <h1 className="text-3xl font-bold font-[Chivo] tracking-tight">Crawl runs</h1>
          <p className="text-sm text-zinc-600 mt-1">Every scrape stores a new run record. Data persists across restarts.</p>
        </div>
        <button onClick={reload} className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {runs.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center text-zinc-500">
          <Database size={32} className="mx-auto mb-3 text-zinc-300" />
          <p>No crawl runs yet. Click <strong>Run Crawl Now</strong> in the top bar to start one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {runs.map(r => {
            const isRunning = r.status === "running" || !r.finished_at;
            const isExpanded = expanded === r.id;
            const dur = <Duration start={r.started_at} end={r.finished_at} />;
            return (
              <div
                key={r.id}
                className={`border bg-white ${isRunning ? "border-[#002FA7] ring-1 ring-[#002FA7]/20" : "border-zinc-200"}`}
                data-testid={`run-row-${r.id}`}
              >
                <div
                  className="flex items-center gap-4 px-5 py-3 cursor-pointer hover:bg-zinc-50"
                  onClick={() => setExpanded(isExpanded ? null : r.id)}
                >
                  <div className="flex-shrink-0">
                    {isRunning ? (
                      <RefreshCw size={16} className="text-[#002FA7] animate-spin" />
                    ) : r.status === "completed" ? (
                      <CheckCircle size={16} className="text-[#00B140]" />
                    ) : (
                      <XCircle size={16} className="text-[#E00000]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">Started</div>
                      <Fmt value={r.started_at} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">Duration</div>
                      <span className="font-mono text-xs">{dur}</span>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">Status</div>
                      <span className={`text-[10px] uppercase tracking-widest font-mono font-bold ${
                        isRunning ? "text-[#002FA7]" :
                        r.status === "completed" ? "text-[#00B140]" : "text-[#E00000]"
                      }`}>{isRunning ? "Running..." : r.status}</span>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">Trigger</div>
                      <span className="text-xs font-mono capitalize">{r.triggered_by || "api"}</span>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">Results</div>
                      <span className="text-xs font-mono">{r.opportunities_found} found · {r.opportunities_new} new · {r.opportunities_updated} updated</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-zinc-300 text-zinc-500 flex-shrink-0">
                    {r.documents_downloaded > 0 ? `${r.documents_downloaded} doc(s)` : "0 docs"}
                  </Badge>
                </div>
                {isExpanded && (
                  <div className="border-t border-zinc-100 px-5 py-4 bg-zinc-50 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1 flex items-center gap-1">
                          <Globe size={11} /> Sources attempted
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(r.sources_attempted || []).map(s => (
                            <Badge key={s} variant="outline" className="rounded-sm text-[10px] font-mono border-zinc-300 text-zinc-600">
                              {s}
                            </Badge>
                          ))}
                          {(!r.sources_attempted || r.sources_attempted.length === 0) && <span className="text-xs text-zinc-400">None</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1 flex items-center gap-1">
                          <CheckCircle size={11} className="text-[#00B140]" /> Sources succeeded
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(r.sources_succeeded || []).map(s => (
                            <Badge key={s} variant="outline" className="rounded-sm text-[10px] font-mono border-green-200 text-green-700 bg-green-50">
                              {s}
                            </Badge>
                          ))}
                          {(!r.sources_succeeded || r.sources_succeeded.length === 0) && <span className="text-xs text-zinc-400">None</span>}
                        </div>
                      </div>
                    </div>
                    {r.errors && r.errors.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#E00000] font-mono mb-1 flex items-center gap-1">
                          <AlertCircle size={11} /> Errors ({r.errors.length})
                        </div>
                        <ul className="text-xs text-[#E00000] space-y-0.5">
                          {r.errors.map((e, i) => <li key={i} className="font-mono">• {e}</li>)}
                        </ul>
                      </div>
                    )}
                    <div className="text-[10px] text-zinc-400 font-mono">Run ID: {r.id}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 border border-zinc-200 bg-[#FAFAFA] p-4 text-sm text-zinc-600">
        <div className="flex items-center gap-2 mb-1">
          <Database size={14} className="text-zinc-400" />
          <span className="font-medium text-zinc-800 text-xs uppercase tracking-widest font-mono">Data persistence</span>
        </div>
        <p className="text-xs">Every crawl run is stored in MongoDB (<span className="font-mono">crawl_runs</span> collection). Records persist across server restarts — the full history is always available here.</p>
      </div>
    </div>
  );
}
